<template>
  <div class="signature-pad-container">
    <!-- 操作ボタン -->
    <div class="signature-pad-actions mb-2 text-right">
      <button type="button" class="btn btn-outline-secondary btn-sm" @click="clear">
        クリア
      </button>
      <button type="button" class="btn btn-outline-secondary btn-sm ms-2" @click="undo">
        元に戻す
      </button>
    </div>
    <!-- 署名を描画するためのキャンバス -->
    <canvas
      ref="canvas"
      class="signature-pad-canvas"
      :style="{ width: width, height: height }"
    ></canvas>

  </div>
</template>

<script lang="ts">
import SignaturePad from 'signature_pad'
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'SignaturePad',
  props: {
    /**
     * キャンバスの幅
     */
    width: {
      type: String,
      default: '100%',
    },
    /**
     * キャンバスの高さ
     */
    height: {
      type: String,
      default: '250px',
    },
    /**
     * signature_padライブラリに渡すオプション
     * @see https://github.com/szimek/signature_pad
     */
    options: {
      type: Object,
      default: () => ({
        penColor: 'rgb(0, 0, 0)',
      }),
    },
  },
  emits: [
    /**
     * 署名の描画を開始したときに発火します
     */
    'begin',
    /**
     * 署名の描画を終了したときに発火します
     */
    'end',
    /**
     * 署名をクリアしたときに発火します
     */
    'clear',
  ],
  data() {
    return {
      /**
       * SignaturePadのインスタンスを保持します
       * @type {SignaturePad | null}
       */
      signaturePad: null as SignaturePad | null,
    }
  },
  mounted() {
    this.initialize()
    // ウィンドウのリサイズを監視し、キャンバスのサイズを調整する
    window.addEventListener('resize', this.resizeCanvas)
  },
  beforeUnmount() {
    // コンポーネントが破棄される前に、リサイズイベントのリスナーを解除する
    window.removeEventListener('resize', this.resizeCanvas)
  },
  methods: {
    /**
     * SignaturePadを初期化します
     */
    initialize() {
      const canvas = this.$refs.canvas as HTMLCanvasElement
      if (!canvas) {
        console.error('Canvas element not found.')
        return
      }

      // SignaturePadインスタンスを作成
      this.signaturePad = new SignaturePad(canvas, this.options)

      // イベントリスナーを設定
      this.signaturePad.addEventListener('beginStroke', this.onBeginStroke)
      this.signaturePad.addEventListener('endStroke', this.onEndStroke)

      // キャンバスのサイズを初期設定
      this.resizeCanvas()
    },

    /**
     * キャンバスのサイズを親要素に合わせて調整します。
     * これにより、高DPIディスプレイでも署名が鮮明に表示されます。
     */
    resizeCanvas() {
      const canvas = this.$refs.canvas as HTMLCanvasElement
      if (!canvas || !this.signaturePad) return

      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      const rect = canvas.getBoundingClientRect()

      canvas.width = rect.width * ratio
      canvas.height = rect.height * ratio
      canvas.getContext('2d')?.scale(ratio, ratio)

      // サイズ変更後に既存の描画内容を復元する
      const data = this.signaturePad.toData()
      this.signaturePad.clear() // clearは内部的にcanvasをリセットする
      this.signaturePad.fromData(data)
    },

    /**
     * 署名をクリアします
     */
    clear() {
      if (this.signaturePad) {
        this.signaturePad.clear()
        this.$emit('clear')
      }
    },

    /**
     * 直前のストロークを元に戻します
     */
    undo() {
      if (this.signaturePad) {
        const data = this.signaturePad.toData()
        if (data && data.length > 0) {
          data.pop() // 最後のストロークを削除
          this.signaturePad.fromData(data)
        }
      }
    },

    /**
     * 署名が空かどうかを返します
     * @returns {boolean} 空の場合はtrue
     */
    isEmpty(): boolean {
      return this.signaturePad ? this.signaturePad.isEmpty() : true
    },

    /**
     * 署名データを指定された形式で取得します
     * @param {string} [type='image/png'] - データ形式 (例: 'image/png', 'image/jpeg', 'image/svg+xml')
     * @param {number} [encoderOptions] - 'image/jpeg'または'image/webp'の場合の画質 (0から1)
     * @returns {string | undefined} データURL
     */
    getSignatureData(
      type: string = 'image/png',
      encoderOptions?: number,
    ): string | undefined {
      if (this.signaturePad && !this.isEmpty()) {
        return this.signaturePad.toDataURL(type, encoderOptions)
      }
      return undefined
    },

    /**
     * 描画開始時のイベントハンドラ
     */
    onBeginStroke() {
      this.$emit('begin')
    },

    /**
     * 描画終了時のイベントハンドラ
     */
    onEndStroke() {
      this.$emit('end', this.getSignatureData())
    },
  },
})
</script>

<style scoped>
.signature-pad-container {
  position: relative;
  width: 100%;
  height: auto;
}

.signature-pad-canvas {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  touch-action: none; /* モバイルでのスクロールを防止 */
}
</style>
